import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import get from 'lodash/get';

import { formatCurrency, fullName } from 'utils';
import Container from 'common/container';
import FormField from 'common/form-field';
import Button from 'common/button';
import Link from 'common/link';
import ContributeContext from '../context';
import CardField from './card-field';
import ThirdPartyTerms from './third-party-terms';

const SelectAmount = ({ isSubmitting, onThirdPartyTermsChange, onSubmit }) => {
    const { total, campaign, user, token, thirdPartyTermsAccepted } = useContext(ContributeContext);
    const { collectAddresses } = campaign.recipient;

    // Memoize the initial values so Formik doesn't break on re-renders
    const initialValues = useMemo(
        () => ({
            token,
            user: {
                firstName: user?.firstName ?? '',
                lastName: user?.lastName ?? '',
                email: user?.email ?? '',
                ...(collectAddresses
                    ? {
                          address: {
                              street1: user?.address?.street1 ?? '',
                              street2: user?.address?.street2 ?? '',
                              city: user?.address?.city ?? '',
                              stateProv: user?.address?.stateProv ?? '',
                              postalCode: user?.address?.postalCode ?? '',
                          },
                      }
                    : {}),
            },
        }),
        [collectAddresses, user, token]
    );

    const validationSchema = yup.object().shape({
        token: yup
            .string()
            .required('Valid payment source required')
            .nullable(),
        user: yup.object().shape({
            firstName: yup.string().required('This field is required'),
            lastName: yup.string().required('This field is required'),
            email: yup
                .string()
                .email('Invalid email address')
                .required('This field is required'),
            ...(collectAddresses
                ? {
                      address: yup.object().shape({
                          street1: yup.string().required('This field is required'),
                          street2: yup.string(),
                          city: yup.string().required('This field is required'),
                          stateProv: yup.string().required('This field is required'),
                          postalCode: yup.string().required('This field is required'),
                      }),
                  }
                : {}),
        }),
    });

    return (
        <Container className="pt-8 pb-16 flex-1 flex flex-col justify-start max-w-md">
            <div className="mt-12 rounded-lg lg:bg-white lg:shadow lg:p-16">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={onSubmit}
                    validateOnMount
                >
                    {({
                        isValid,
                        values,
                        touched,
                        errors,
                        setFieldTouched,
                        setFieldError,
                        setFieldValue,
                    }) => {
                        const getFieldInvalid = field =>
                            Boolean(get(touched, field) && get(errors, field));

                        return (
                            <Form>
                                <div className="-mt-16">
                                    <fieldset className="pt-16">
                                        <h2 className="text-gray-80 font-black text-21 md:text-24 mb-8">
                                            Personal Information
                                        </h2>

                                        <div className="md:flex -ml-4 -mt-4">
                                            <div className="md:w-1/2 pl-4 pt-4">
                                                <Field
                                                    as={FormField}
                                                    label="First name"
                                                    name="user.firstName"
                                                    invalid={getFieldInvalid('user.firstName')}
                                                />
                                            </div>
                                            <div className="md:w-1/2 pl-4 pt-4">
                                                <Field
                                                    as={FormField}
                                                    label="Last name"
                                                    name="user.lastName"
                                                    invalid={getFieldInvalid('user.lastName')}
                                                />
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <Field
                                                as={FormField}
                                                label="Email address"
                                                name="user.email"
                                                type="email"
                                                invalid={getFieldInvalid('user.email')}
                                            />
                                            <ErrorMessage
                                                component="p"
                                                name="user.email"
                                                className="text-12 text-red-70 mt-2"
                                            />
                                        </div>
                                        {collectAddresses && (
                                            <>
                                                <div className="mt-12">
                                                    <Field
                                                        as={FormField}
                                                        label="Address 1"
                                                        name="user.address.street1"
                                                        invalid={getFieldInvalid(
                                                            'user.address.street1'
                                                        )}
                                                    />
                                                </div>
                                                <div className="mt-4">
                                                    <Field
                                                        as={FormField}
                                                        label="Address 2"
                                                        name="user.address.street2"
                                                        invalid={getFieldInvalid(
                                                            'user.address.street2'
                                                        )}
                                                    />
                                                </div>
                                                <div className="mt-4">
                                                    <Field
                                                        as={FormField}
                                                        label="City"
                                                        name="user.address.city"
                                                        invalid={getFieldInvalid(
                                                            'user.address.city'
                                                        )}
                                                    />
                                                </div>
                                                <div className="flex -ml-4 mt-4">
                                                    <div className="w-1/2 pl-4">
                                                        <Field
                                                            as={FormField}
                                                            label="State"
                                                            name="user.address.stateProv"
                                                            invalid={getFieldInvalid(
                                                                'user.address.stateProv'
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="w-1/2 pl-4">
                                                        <Field
                                                            as={FormField}
                                                            label="Zip"
                                                            name="user.address.postalCode"
                                                            invalid={getFieldInvalid(
                                                                'user.address.postalCode'
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </fieldset>
                                    <fieldset className="pt-16">
                                        <h2 className="text-gray-80 font-black text-21 md:text-24 mb-8">
                                            Billing Information
                                        </h2>
                                        <CardField
                                            name={fullName(
                                                values.user.firstName,
                                                values.user.lastName
                                            )}
                                            onChange={value => {
                                                setFieldTouched('token', true);
                                                setFieldValue('token', value);
                                            }}
                                            onError={error => {
                                                console.error('Card field error:', error);
                                                setFieldTouched('token', true);
                                                setFieldError('token', 'Invalid card information');
                                            }}
                                        />
                                        <ErrorMessage
                                            component="p"
                                            name="token"
                                            className="text-12 text-red-70 mt-2"
                                        />
                                    </fieldset>

                                    <div className="mt-12 md:mt-16 text-center">
                                        <p className="text-18 md:text-21 text-gray-80">
                                            Estimated Total:{' '}
                                            <strong>{formatCurrency(total)}</strong>
                                        </p>
                                        <Button
                                            type="submit"
                                            color={isValid ? 'green' : 'light'}
                                            size="md"
                                            className={cx('mt-8 text-16 font-medium block w-full', {
                                                'pointer-events-none': !isValid || isSubmitting,
                                            })}
                                        >
                                            {isSubmitting ? 'Submitting...' : 'Submit Payment'}
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
            <div className="mt-8">
                {campaign.thirdPartyTerms?.statement && (
                    <div className="mb-8">
                        <ThirdPartyTerms
                            isAccepted={thirdPartyTermsAccepted}
                            statement={campaign.thirdPartyTerms.statement}
                            fullTerms={campaign.thirdPartyTerms.extended}
                            onChange={onThirdPartyTermsChange}
                        />
                    </div>
                )}
                <p className="font-medium text-center text-12 text-gray-40">
                    By confirming you agree to Pledge It’s{' '}
                    <Link to="/tos" className="link-green" external>
                        Terms of Service
                    </Link>
                </p>
            </div>
        </Container>
    );
};

SelectAmount.propTypes = {
    onThirdPartyTermsChange: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
};

SelectAmount.defaultProps = {
    isSubmitting: false,
};

export default SelectAmount;
